import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import IDCardFields from "../FormComponents/IDCardFields";
import {
  fetchIDCardDetailsByParent,
  addIDCardData,
  editIDCardData,
  deleteIDCardData,
} from "../../services/idCardService";

/**
 * Add ID Card Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onIDCardAdded - Callback after ID card is added successfully
 */
export const AddIDCardDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onIDCardAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleClear = () => {
    setFormData({
      idtype1: "",
      idcardname1: "",
      idnumber1: "",
      issuedate1: "",
      expirydate1: "",
      idcarddescription1: "",
      image11: "",
      image21: "",
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const idCardData = {
        idtype: formData.idtype1,
        idcardname: formData.idcardname1,
        idnumber: formData.idnumber1,
        issuedate: formData.issuedate1,
        expirydate: formData.expirydate1,
        idcarddescription: formData.idcarddescription1,
        image1: formData.image11,
        image2: formData.image21,
        parentid: userId,
      };

      await addIDCardData(sessionId, idCardData);

      // Fetch updated list
      const updatedIDCardEntries = await fetchIDCardDetailsByParent(sessionId, userId);
      
      if (onIDCardAdded) {
        onIDCardAdded(updatedIDCardEntries);
      }

      handleClear();
      onClose();
      
      if (onSuccess) {
        onSuccess("ID card added successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to add ID card. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              add_card
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add New ID Card
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <IDCardFields
            formData={{
              idtype1: formData.idtype1 || "",
              idcardname1: formData.idcardname1 || "",
              idnumber1: formData.idnumber1 || "",
              issuedate1: formData.issuedate1 || "",
              expirydate1: formData.expirydate1 || "",
              idcarddescription1: formData.idcarddescription1 || "",
              image11: formData.image11 || "",
              image21: formData.image21 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add ID Card"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit ID Card Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} idCardEntry - ID card entry to edit
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onIDCardUpdated - Callback after ID card is updated successfully
 */
export const EditIDCardDialog = ({
  open,
  onClose,
  idCardEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onIDCardUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load ID card data when dialog opens
  React.useEffect(() => {
    if (open && idCardEntry) {
   
      
      setFormData({
        idtype1: idCardEntry.idtype || "",
        idcardname1: idCardEntry.idcardname || idCardEntry.name || "",
        idnumber1: idCardEntry.idnumber || "",
        issuedate1: idCardEntry.issuedate || "",
        expirydate1: idCardEntry.expirydate || "",
        idcarddescription1: idCardEntry.idcarddescription || idCardEntry.description || "",
        image11: idCardEntry.image1 || "",
        image21: idCardEntry.image2 || "",
      });
      
    }
  }, [open, idCardEntry]);

  const handleClear = () => {
    setFormData({
      idtype1: "",
      idcardname1: "",
      idnumber1: "",
      issuedate1: "",
      expirydate1: "",
      idcarddescription1: "",
      image11: "",
      image21: "",
    });
  };

  const handleSave = async () => {
    if (!idCardEntry || !idCardEntry.id) return;

    setIsLoading(true);
    try {
      const idCardData = {
        idtype: formData.idtype1,
        idcardname: formData.idcardname1,
        idnumber: formData.idnumber1,
        issuedate: formData.issuedate1,
        expirydate: formData.expirydate1,
        idcarddescription: formData.idcarddescription1,
        image1: formData.image11,
        image2: formData.image21,
        parentid: userId,
      };

      await editIDCardData(sessionId, idCardEntry.id, idCardData);

      // Fetch updated list
      const updatedIDCardEntries = await fetchIDCardDetailsByParent(sessionId, userId);
      
      if (onIDCardUpdated) {
        onIDCardUpdated(updatedIDCardEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("ID card updated successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to update ID card. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="primary" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit ID Card
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <IDCardFields
            formData={{
              idtype1: formData.idtype1 || "",
              idcardname1: formData.idcardname1 || "",
              idnumber1: formData.idnumber1 || "",
              issuedate1: formData.issuedate1 || "",
              expirydate1: formData.expirydate1 || "",
              idcarddescription1: formData.idcarddescription1 || "",
              image11: formData.image11 || "",
              image21: formData.image21 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete ID Card Confirmation Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} idCardEntry - ID card entry to delete
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onIDCardDeleted - Callback after ID card is deleted successfully
 */
export const DeleteIDCardDialog = ({
  open,
  onClose,
  idCardEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onIDCardDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!idCardEntry || !idCardEntry.id) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await deleteIDCardData(sessionId, idCardEntry.id);

      // Fetch updated list
      const updatedIDCardEntries = await fetchIDCardDetailsByParent(sessionId, userId);
      
      if (onIDCardDeleted) {
        onIDCardDeleted(updatedIDCardEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("ID card deleted successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to delete ID card. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete this ID card?
        </MDTypography>
        {idCardEntry && (
          <MDBox
            mt={2}
            p={2}
            sx={{ backgroundColor: "grey.100", borderRadius: 1 }}
          >
            <MDTypography variant="body2" fontWeight="medium">
              Type: {idCardEntry.idtype || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Name: {idCardEntry.idcardname || idCardEntry.name || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Number: {idCardEntry.idnumber ? "••••••••" : "N/A"}
            </MDTypography>
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="error"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
